import axios from 'axios';
import {API_BASE_URL} from './authApi';
import {UserPlanTier} from '../utils/plan';

interface CheckoutSessionResponse {
  success: boolean;
  url: string;
  sessionId: string;
}

interface PortalSessionResponse {
  success: boolean;
  url: string;
}

interface CheckoutSessionVerificationResponse {
  success: boolean;
  verification: {
    sessionId: string;
    status: string | null;
    paymentStatus: string | null;
    completed: boolean;
    planTier: UserPlanTier;
    subscriptionStatus: string | null;
  };
}

interface BillingSubscriptionResponse {
  success: boolean;
  subscription: BillingSubscriptionSummary;
}

export interface BillingSubscriptionSummary {
  planTier: UserPlanTier;
  subscriptionStatus: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  managementUrlAvailable: boolean;
}

type BillingApiErrorResponse = {
  message?: string;
  error?: string | {message?: string};
};

const billingClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as BillingApiErrorResponse | undefined;
    const nestedError =
      typeof errorData?.error === 'string'
        ? errorData.error
        : errorData?.error?.message;

    return errorData?.message || nestedError || error.message || fallbackMessage;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallbackMessage;
};

const authorizedHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

export const billingApi = {
  async createCheckoutSession(token: string) {
    try {
      const response = await billingClient.post<CheckoutSessionResponse>(
        '/billing/checkout-sessions',
        {},
        {
          headers: authorizedHeaders(token),
        }
      );

      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to start Stripe checkout'));
    }
  },

  async verifyCheckoutSession(token: string, sessionId: string) {
    try {
      const response = await billingClient.get<CheckoutSessionVerificationResponse>(
        `/billing/checkout-sessions/${encodeURIComponent(sessionId)}`,
        {
          headers: authorizedHeaders(token),
        }
      );

      return response.data.verification;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to verify Stripe checkout'));
    }
  },

  async getSubscriptionSummary(token: string) {
    try {
      const response = await billingClient.get<BillingSubscriptionResponse>(
        '/billing/subscription',
        {
          headers: authorizedHeaders(token),
        }
      );

      return response.data.subscription;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to load billing status'));
    }
  },

  async createPortalSession(token: string) {
    try {
      const response = await billingClient.post<PortalSessionResponse>(
        '/billing/portal-sessions',
        {},
        {
          headers: authorizedHeaders(token),
        }
      );

      return response.data.url;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to open Stripe billing portal'));
    }
  },

  async getProUpgradeLink(token: string): Promise<string> {
    try {
      const response = await billingClient.get<{success: boolean; url: string}>(
        '/billing/pro-upgrade-link',
        {
          headers: authorizedHeaders(token),
        }
      );

      return response.data.url;
    } catch (error) {
      throw new Error(getErrorMessage(error, 'Failed to start Stripe checkout'));
    }
  },
};
